// @google/genai guidelines followed for service initialization and usage.
import i18n from "../i18n";
import { Coordinates, CampingSpot, MapBounds, MountainRoad, WastePoint } from "../types";
import { fetchNVDBData, NVDBObject } from "./nvdbService";
import { getDistance, checkMinDistance, isPointInPolygon } from "./geometryUtils";
import { getCache, getCacheStale, setCache, CACHE_TTL } from "./cacheService";
import { snapBboxToGrid } from "../utils/boundsUtils";
import { deriveRoadAccessClass } from "./roadAccessClassifier";

import { BRAND } from '../constants/mapColors';
// Highway types that are driveable by car/campervan
const DRIVEABLE_HIGHWAYS = new Set([
  'motorway', 'trunk', 'primary', 'secondary', 'tertiary',
  'residential', 'unclassified', 'service', 'track',
  'motorway_link', 'trunk_link', 'primary_link', 'secondary_link', 'tertiary_link',
  'living_street'
]);

// Sett VITE_OVERPASS_URL for å peke på egen/betalt Overpass-instans. Den prøves FØRST
// (unngår de offentlige serverneres 429/504 rate-limiting), med de offentlige som
// fallback. Se docs/self-hosted-routing.md.
const SELF_HOSTED_OVERPASS = (import.meta as any).env?.VITE_OVERPASS_URL as string | undefined;
const OVERPASS_ENDPOINTS = [
  ...(SELF_HOSTED_OVERPASS ? [SELF_HOSTED_OVERPASS] : []),
  'https://overpass-api.de/api/interpreter',
  'https://lz4.overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter'
];

// Mapping fra norske ord til OSM tags
export const CATEGORY_MAP: Record<string, { tags: string, icon: string, labelKey: string, color: string, categoryKey: string }> = {
  "toalett": { tags: 'node["amenity"="toilets"];way["amenity"="toilets"]', icon: "fa-solid fa-restroom", labelKey: "spot.categories.toilets", color: "#3b82f6", categoryKey: "toilets" },
  "toaletter": { tags: 'node["amenity"="toilets"];way["amenity"="toilets"]', icon: "fa-solid fa-restroom", labelKey: "spot.categories.toilets", color: "#3b82f6", categoryKey: "toilets" },
  "badeplass": { tags: 'node["leisure"="swimming_area"];node["sport"="swimming"];way["leisure"="swimming_area"]', icon: "fa-solid fa-person-swimming", labelKey: "spot.categories.swimming", color: "#0ea5e9", categoryKey: "swimming" },
  "badeplasser": { tags: 'node["leisure"="swimming_area"];node["sport"="swimming"];way["leisure"="swimming_area"]', icon: "fa-solid fa-person-swimming", labelKey: "spot.categories.swimming", color: "#0ea5e9", categoryKey: "swimming" },
  "isbre": { tags: 'node["natural"="glacier"];way["natural"="glacier"]', icon: "fa-solid fa-snowflake", labelKey: "spot.categories.glacier", color: "#93c5fd", categoryKey: "glacier" },
  "isbreer": { tags: 'node["natural"="glacier"];way["natural"="glacier"]', icon: "fa-solid fa-snowflake", labelKey: "spot.categories.glacier", color: "#93c5fd", categoryKey: "glacier" },
  "butikk": { tags: 'node["shop"~"supermarket|convenience"];way["shop"~"supermarket|convenience"]', icon: "fa-solid fa-cart-shopping", labelKey: "spot.categories.shop", color: BRAND, categoryKey: "shop" },
  "matbutikk": { tags: 'node["shop"~"supermarket|convenience"];way["shop"~"supermarket|convenience"]', icon: "fa-solid fa-cart-shopping", labelKey: "spot.categories.shop", color: BRAND, categoryKey: "shop" },
  "bensinstasjon": { tags: 'node["amenity"="fuel"];way["amenity"="fuel"]', icon: "fa-solid fa-gas-pump", labelKey: "spot.categories.fuel", color: "#f59e0b", categoryKey: "fuel" },
  "vann": { tags: 'node["amenity"="drinking_water"]', icon: "fa-solid fa-droplet", labelKey: "spot.categories.water", color: "#3b82f6", categoryKey: "water" },
  "drikkevann": { tags: 'node["amenity"="drinking_water"]', icon: "fa-solid fa-droplet", labelKey: "spot.categories.water", color: "#3b82f6", categoryKey: "water" },
  "dusj": { tags: 'node["amenity"="shower"]', icon: "fa-solid fa-shower", labelKey: "spot.categories.shower", color: "#6366f1", categoryKey: "shower" },
  "tømmestasjon": { tags: 'node["amenity"="sanitary_dump_station"]', icon: "fa-solid fa-faucet-dotted", labelKey: "spot.categories.dump_station", color: "#8b5cf6", categoryKey: "dump_station" },
  "søppel": { tags: 'node["amenity"~"waste_basket|waste_disposal"]', icon: "fa-solid fa-trash", labelKey: "spot.categories.trash", color: "#64748b", categoryKey: "trash" },
  "apotek": { tags: 'node["amenity"="pharmacy"]', icon: "fa-solid fa-capsules", labelKey: "spot.categories.pharmacy", color: "#ef4444", categoryKey: "pharmacy" },
  "utkikkspunkt": { tags: 'node["tourism"="viewpoint"]', icon: "fa-solid fa-mountain", labelKey: "spot.categories.viewpoint", color: BRAND, categoryKey: "viewpoint" },
  "lekeplass": { tags: 'node["leisure"="playground"];way["leisure"="playground"]', icon: "fa-solid fa-child-reaching", labelKey: "spot.categories.playground", color: "#f59e0b", categoryKey: "playground" },
  "lekeplasser": { tags: 'node["leisure"="playground"];way["leisure"="playground"]', icon: "fa-solid fa-child-reaching", labelKey: "spot.categories.playground", color: "#f59e0b", categoryKey: "playground" }
};

export let lastOverpassDebug = {
  lastUrl: "",
  lastResponse: null as any,
  lastError: null as any,
  lastQuery: "",
  candidateCount: 0,
  filteredCount: 0,
  timestamp: 0,
  duration: 0
};

// Added lastCoastalDebug export to satisfy DebugConsole.tsx import
export let lastCoastalDebug = {
  lastUrl: "",
  lastResponse: null as any,
  lastError: null as any,
  lastQuery: "",
  candidateCount: 0,
  timestamp: 0,
  duration: 0
};

let globalOverpassQueue: Promise<any> = Promise.resolve();
let lastGlobalOverpassAt = 0;
let overpassPaused = false;

// Pause all Overpass requests when tab is hidden to prevent 429 storms on resume
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    overpassPaused = document.hidden;
  });
}

const OVERPASS_MIN_GAP_MS = 3000; // Increased from 1.5s to avoid 429s
const OVERPASS_WARN_THROTTLE_MS = 60_000;
const OVERPASS_ERROR_COOLDOWN_MS = 60_000; // Increased from 45s

const overpassInFlight = new Map<string, Promise<any>>();
const overpassCooldownUntil = new Map<string, number>();
const overpassWarnedAt = new Map<string, number>();

const normalizeOverpassQuery = (query: string) => query.replace(/\s+/g, ' ').trim();

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const warnOverpassOnce = (key: string, message: string) => {
  const now = Date.now();
  const lastWarned = overpassWarnedAt.get(key) || 0;
  if (now - lastWarned < OVERPASS_WARN_THROTTLE_MS) return;
  overpassWarnedAt.set(key, now);
  console.warn(message);
};

const isAbortLikeError = (error: any) =>
  error?.name === 'AbortError' || error?.message === 'AbortError';

/** Direct Overpass fetch that bypasses the global serial queue.
 *  Use for user-initiated actions (like "Vis Veg") that need fast response.
 *  Still respects a minimum gap to avoid 429s. */
const directOverpassFetch = async (query: string): Promise<any> => {
  const waitMs = Math.max(0, OVERPASS_MIN_GAP_MS - (Date.now() - lastGlobalOverpassAt));
  if (waitMs > 0) await sleep(waitMs);
  lastGlobalOverpassAt = Date.now();

  for (const endpoint of OVERPASS_ENDPOINTS) {
    try {
      const res = await fetch(`${endpoint}?data=${encodeURIComponent(query)}`, { signal: AbortSignal.timeout(10000) });
      if (res.status === 429) {
        await sleep(6000);
        continue;
      }
      if (!res.ok) continue;
      const text = await res.text();
      if (text.trimStart().startsWith('<')) continue;
      return JSON.parse(text);
    } catch { continue; }
  }
  throw new Error('All Overpass endpoints failed');
};

/** Direct Overpass fetch that bypasses the global serial queue, with abort signal support.
 *  Use for Discover/background UI queries that must not be blocked by map-triggered queries.
 *  Combines the caller's signal with a 20-second hard timeout so the fetch always resolves. */
export const directOverpassFetchWithSignal = async (query: string, signal?: AbortSignal): Promise<any> => {
  const waitMs = Math.max(0, OVERPASS_MIN_GAP_MS - (Date.now() - lastGlobalOverpassAt));
  if (waitMs > 0) {
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(resolve, waitMs);
      signal?.addEventListener('abort', () => { clearTimeout(timer); reject(new DOMException('AbortError', 'AbortError')); }, { once: true });
    });
  }
  if (signal?.aborted) throw new DOMException('AbortError', 'AbortError');
  lastGlobalOverpassAt = Date.now();

  // Always apply a hard 20-second timeout, even when a caller signal is provided
  const effectiveSignal = signal
    ? AbortSignal.any([signal, AbortSignal.timeout(20000)])
    : AbortSignal.timeout(20000);

  for (const endpoint of OVERPASS_ENDPOINTS) {
    try {
      const res = await fetch(`${endpoint}?data=${encodeURIComponent(query)}`, { signal: effectiveSignal });
      if (res.status === 429) {
        await sleep(3000);
        continue;
      }
      if (!res.ok) continue;
      const text = await res.text();
      if (text.trimStart().startsWith('<')) continue;
      return JSON.parse(text);
    } catch (e: any) {
      if (isAbortLikeError(e)) throw e;
      continue;
    }
  }
  throw new Error('All Overpass endpoints failed');
};

export const robustOverpassQuery = async (query: string, debugObj: any = lastOverpassDebug, signal?: AbortSignal): Promise<any> => {
  const queryKey = normalizeOverpassQuery(query);
  if (!signal) {
    const inFlight = overpassInFlight.get(queryKey);
    if (inFlight) return inFlight;
  }

  const requestPromise = new Promise((resolve, reject) => {
    globalOverpassQueue = globalOverpassQueue
      .catch(() => undefined)
      .then(async () => {
        // Wait until tab is visible again before making API calls
        if (overpassPaused) {
          let waited = 0;
          while (overpassPaused && waited < 30000) { await sleep(500); waited += 500; }
        }

        const cooldownUntil = overpassCooldownUntil.get(queryKey) || 0;
        if (cooldownUntil > Date.now()) {
          reject(new Error(`Overpass-kallet er midlertidig nedkjølt for denne spørringen.`));
          return;
        }

        const waitMs = Math.max(0, OVERPASS_MIN_GAP_MS - (Date.now() - lastGlobalOverpassAt));
        if (waitMs > 0) await sleep(waitMs);
        lastGlobalOverpassAt = Date.now();

        const startTime = Date.now();
        debugObj.lastQuery = query;
        debugObj.timestamp = startTime;
        debugObj.lastResponse = null;
        debugObj.lastError = null;

        let lastErr = null;
        let backoffMs = 6000;

        for (let ei = 0; ei < OVERPASS_ENDPOINTS.length; ei++) {
          const endpoint = OVERPASS_ENDPOINTS[ei];
          // Allow up to 2 retries on same endpoint for 429
          for (let retry = 0; retry < 2; retry++) {
            try {
              if (signal?.aborted) throw new Error('AbortError');

              const url = `${endpoint}?data=${encodeURIComponent(query)}`;
              debugObj.lastUrl = url;

              const res = await fetch(url, { signal: signal ?? AbortSignal.timeout(25000) });

              if (res.status === 429) {
                lastErr = `429 Too Many Requests fra ${endpoint}`;
                overpassCooldownUntil.set(queryKey, Date.now() + OVERPASS_ERROR_COOLDOWN_MS);
                warnOverpassOnce(`${endpoint}:429`, `${lastErr}. Venter ${backoffMs / 1000}s...`);
                await sleep(backoffMs);
                backoffMs = Math.min(backoffMs * 2, 30000);
                continue; // retry same endpoint
              }

              if (!res.ok) {
                lastErr = `Server ${endpoint} failed (${res.status})`;
                if (res.status >= 500) {
                  overpassCooldownUntil.set(queryKey, Date.now() + OVERPASS_ERROR_COOLDOWN_MS);
                }
                break; // try next endpoint
              }

              const text = await res.text();
              if (text.trimStart().startsWith('<')) {
                lastErr = `Server ${endpoint} returned HTML/XML instead of JSON`;
                overpassCooldownUntil.set(queryKey, Date.now() + OVERPASS_ERROR_COOLDOWN_MS);
                warnOverpassOnce(`${endpoint}:html`, lastErr);
                break; // try next endpoint
              }

              const data = JSON.parse(text);
              overpassCooldownUntil.delete(queryKey);
              debugObj.lastResponse = data;
              debugObj.duration = Date.now() - startTime;
              resolve(data);
              return;
            } catch (e: any) {
              if (isAbortLikeError(e)) { reject(e); return; }
              lastErr = e?.message || `Overpass endpoint ${endpoint} failed`;
              warnOverpassOnce(`${endpoint}:${lastErr}`, `Overpass endpoint ${endpoint} failed: ${lastErr}`);
              break; // try next endpoint
            }
          }
        }
        reject(new Error(lastErr || i18n.t('overpass.server_error')));
      })
      .catch(() => undefined);
  });

  if (!signal) {
    const sharedPromise = requestPromise.finally(() => {
      if (overpassInFlight.get(queryKey) === sharedPromise) {
        overpassInFlight.delete(queryKey);
      }
    });
    overpassInFlight.set(queryKey, sharedPromise);
    return sharedPromise;
  }

  return requestPromise;
};

/**
 * Identifies a road at a clicked location and fetches the full geometry 
 * of that road (by name/ref) within the current view bounds.
 */
export const fetchRoadFromClick = async (
  clickCoords: Coordinates,
  bounds: MapBounds,
  searchRadiusMeters: number = 20
): Promise<any | null> => {
  const lat = clickCoords.latitude;
  const lon = clickCoords.longitude;
  const bbox = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  const radius = Math.max(5, Math.round(searchRadiusMeters));

  try {
    // Single query: fetch nearby road WITH geometry, then expand by name/ref if found
    const nearbyQuery = `[out:json][timeout:10];
      way(around:${radius}, ${lat}, ${lon})["highway"];
      out geom tags;`;

    // Use direct fetch (bypasses global queue) for fast user-initiated response
    const nearbyData = await directOverpassFetch(nearbyQuery);
    const nearbyElements = nearbyData.elements || [];

    if (nearbyElements.length === 0) return null;

    const road = nearbyElements[0];
    const tags = road.tags || {};
    const roadIdentifier = tags.name || tags.ref;

    // If road has a name/ref, fetch all segments with that name in the visible bbox
    // (second query only needed to show the full road extent)
    let allFeatures: any[];
    if (roadIdentifier) {
      const safeName = roadIdentifier.replace(/"/g, '\\"');
      const fullRoadQuery = `[out:json][timeout:15];
        (
          way["name"="${safeName}"](${bbox});
          way["ref"="${safeName}"](${bbox});
        );
        out geom;`;

      const fullData = await directOverpassFetch(fullRoadQuery);
      allFeatures = (fullData.elements || []);
    } else {
      // Unnamed road: use the nearby results directly (already have geometry)
      allFeatures = nearbyElements;
    }

    const features = allFeatures.map((e: any) => {
      if (!e.geometry) return null;
      return {
        type: "Feature",
        properties: e.tags,
        geometry: {
          type: "LineString",
          coordinates: e.geometry.map((p: any) => [p.lon, p.lat])
        }
      };
    }).filter(Boolean);

    if (features.length === 0) return null;

    return {
      type: "FeatureCollection",
      features: features
    };

  } catch (e) {
    console.warn("Road identification failed:", e);
    return null;
  }
};

type SnapResult = {
  location: Coordinates;
  snapped: boolean;
  distanceMeters: number;
};

const projectPointToSegment = (p: Coordinates, a: Coordinates, b: Coordinates): Coordinates => {
  const dx = b.longitude - a.longitude;
  const dy = b.latitude - a.latitude;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return a;

  const tRaw = ((p.longitude - a.longitude) * dx + (p.latitude - a.latitude) * dy) / lenSq;
  const t = Math.max(0, Math.min(1, tRaw));
  return {
    latitude: a.latitude + t * dy,
    longitude: a.longitude + t * dx
  };
};

const findNearestPointOnRoadGeometry = (origin: Coordinates, roadGeoJson: any): SnapResult | null => {
  const features = Array.isArray(roadGeoJson?.features) ? roadGeoJson.features : [];
  let bestPoint: Coordinates | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const feature of features) {
    const geometry = feature?.geometry;
    if (!geometry) continue;

    const segments: number[][][] =
      geometry.type === "LineString"
        ? [geometry.coordinates || []]
        : geometry.type === "MultiLineString"
          ? (geometry.coordinates || [])
          : [];

    for (const line of segments) {
      if (!Array.isArray(line) || line.length < 2) continue;
      for (let i = 0; i < line.length - 1; i++) {
        const a = line[i];
        const b = line[i + 1];
        if (!Array.isArray(a) || !Array.isArray(b) || a.length < 2 || b.length < 2) continue;

        const start: Coordinates = { latitude: Number(a[1]), longitude: Number(a[0]) };
        const end: Coordinates = { latitude: Number(b[1]), longitude: Number(b[0]) };
        if (!Number.isFinite(start.latitude) || !Number.isFinite(start.longitude) || !Number.isFinite(end.latitude) || !Number.isFinite(end.longitude)) continue;

        const projected = projectPointToSegment(origin, start, end);
        const d = getDistance(origin.latitude, origin.longitude, projected.latitude, projected.longitude);
        if (d < bestDistance) {
          bestDistance = d;
          bestPoint = projected;
        }
      }
    }
  }

  if (!bestPoint || !Number.isFinite(bestDistance)) return null;
  return { location: bestPoint, snapped: true, distanceMeters: bestDistance };
};

/**
 * Snapper et punkt til nærmeste veigeometri innenfor kartutsnittet.
 * Brukes primært for manuelle bom-punkter slik at markøren havner på vei.
 */
export const snapToNearestRoad = async (
  clickCoords: Coordinates,
  bounds: MapBounds,
  maxSnapMeters: number = 200
): Promise<SnapResult> => {
  try {
    const road = await fetchRoadFromClick(clickCoords, bounds, maxSnapMeters);
    if (!road) {
      return { location: clickCoords, snapped: false, distanceMeters: Number.POSITIVE_INFINITY };
    }

    const nearest = findNearestPointOnRoadGeometry(clickCoords, road);
    if (!nearest) {
      return { location: clickCoords, snapped: false, distanceMeters: Number.POSITIVE_INFINITY };
    }

    if (nearest.distanceMeters > maxSnapMeters) {
      return { location: clickCoords, snapped: false, distanceMeters: nearest.distanceMeters };
    }

    return nearest;
  } catch (error) {
    console.warn("Could not snap point to nearest road:", error);
    return { location: clickCoords, snapped: false, distanceMeters: Number.POSITIVE_INFINITY };
  }
};

export const fetchCategoricalData = async (categoryKey: string, bounds: MapBounds, signal?: AbortSignal): Promise<CampingSpot[]> => {
  const config = CATEGORY_MAP[categoryKey.toLowerCase()];
  if (!config) return [];

  const bbox = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;

  // Formatert spørring for maksimal kompatibilitet
  const query = `[out:json][timeout:30];
    (
      ${config.tags.split(';').map(t => `${t.trim()}(${bbox});`).join('')}
    );
    out center;`;

  try {
    const data = await robustOverpassQuery(query, lastOverpassDebug, signal);
    const elements = data.elements || [];

    return elements.map((e: any) => {
      const lat = e.lat || e.center?.lat;
      const lon = e.lon || e.center?.lon;
      if (!lat || !lon) return null;

      const tags = e.tags || {};
      const localizedLabel = i18n.t(`spot.categories.${config.categoryKey}`);
      const name = tags.name || localizedLabel;

      return {
        id: `search-${e.type}-${e.id}`,
        source: 'osm',
        externalId: `${e.type}-${e.id}`,
        importVersion: 'osm-overpass',
        lastSyncedAt: new Date().toISOString(),
        name: name,
        description: i18n.t('spot.description_search', { category: localizedLabel }),
        location: { latitude: lat, longitude: lon },
        score: 5,
        spotType: 'osm_amenity',
        sourceLevel: 3,
        suitabilityColor: config.color,
        features: ["SØKERESULTAT", localizedLabel.toUpperCase()],
        reasoning: i18n.t('spot.reasoning_map'),
        trafficRisk: "N/A",
        buildingProximity: "N/A",
        nearestHouse: 999,
        highway: "N/A",
        surfaceType: 'Ukjent',
        isPublicAccess: true,
        legal: { isUtmark: true, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false }
      } as CampingSpot;
    }).filter((s): s is CampingSpot => s !== null);
  } catch (e) {
    console.error("Categorical search failed:", e);
    return [];
  }
};

export const fetchMountainRoads = async (bounds: MapBounds, signal?: AbortSignal): Promise<MountainRoad[]> => {
  const cacheKey = `overpass_mtnroads_v1_${snapBboxToGrid(bounds, 0.05)}`;
  const { data: staleData, isStale } = getCacheStale(cacheKey);
  if (staleData && !isStale) return staleData;

  const bbox = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  const query = `[out:json][timeout:30];
    node["natural"="peak"](ele>1000)(${bbox})->.peaks;
    way["highway"~"track|service|unclassified"](around.peaks:2000)(${bbox});
    out geom;`;
  try {
    const data = await robustOverpassQuery(query, { lastQuery: "", lastUrl: "", lastResponse: null, lastError: null }, signal);
    const elements = data.elements || [];
    const result = elements.filter((e: any) => e.type === 'way' && e.geometry).map((e: any) => ({
      id: `mtn-road-${e.id}`,
      name: e.tags?.name || i18n.t('spot.categories.mountain_road'),
      geometry: e.geometry.map((p: any) => ({ latitude: p.lat, longitude: p.lon }))
    }));
    setCache(cacheKey, result, CACHE_TTL.DEFAULT);
    return result;
  } catch (e) { return staleData || []; }
};

export const fetchCoastalSpots = async (bounds: MapBounds, signal?: AbortSignal): Promise<CampingSpot[]> => {
  const cacheKey = `overpass_coastal_v1_${snapBboxToGrid(bounds, 0.05)}`;
  const { data: staleData, isStale } = getCacheStale(cacheKey);
  if (staleData && !isStale) return staleData;

  const bbox = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  const query = `[out:json][timeout:45];
    way["highway"~"motorway|trunk|primary|secondary|tertiary|unclassified|residential|service|track"](${bbox})->.roads;
    (
      node["man_made"~"quay|pier"](around.roads:100)(${bbox});
      way["man_made"~"quay|pier"](around.roads:100)(${bbox});
      node["leisure"="marina"](around.roads:100)(${bbox});
      way["leisure"="marina"](around.roads:100)(${bbox});
    );
    (._; - ._[access~"private|no|restricted|customers|permit"];);
    out center body;`;
  try {
    const data = await robustOverpassQuery(query, lastCoastalDebug, signal);
    const result = (data.elements || []).map((e: any) => {
      const lat = e.lat || e.center?.lat; const lon = e.lon || e.center?.lon;
      if (!lat || !lon) return null;
      const tags = e.tags || {};
      const isQuay = tags.man_made === 'quay' || tags.man_made === 'pier';
      const isMarina = tags.leisure === 'marina';
      const spotName = tags.name || (isQuay ? i18n.t('spot.categories.quay') : isMarina ? i18n.t('spot.categories.marina') : i18n.t('spot.categories.coastal'));
      return {
        id: `osm-maritime-${e.id}`, source: 'osm', externalId: String(e.id), importVersion: 'osm-overpass', lastSyncedAt: new Date().toISOString(), name: spotName, location: { latitude: lat, longitude: lon },
        score: isQuay ? 7 : 6, spotType: 'coastal', sourceLevel: 3, suitabilityColor: '#0288D1',
        features: [i18n.t('spot.features.road_access'), i18n.t('spot.features.quay'), i18n.t('spot.features.water'), isMarina ? i18n.t('spot.features.marina') : ""].filter(Boolean),
        reasoning: i18n.t('spot.reasoning_maritime'), nearestHouse: 999, highway: isQuay ? i18n.t('spot.categories.quay') : i18n.t('spot.categories.marina'),
        description: i18n.t('spot.description_coastal', { name: spotName }),
        trafficRisk: i18n.t('overpass.traffic.low'),
        buildingProximity: "N/A",
        surfaceType: tags.surface || 'Asfalt', isPublicAccess: true,
        legal: { isUtmark: false, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false }
      } as CampingSpot;
    }).filter((s): s is CampingSpot => s !== null);
    setCache(cacheKey, result, CACHE_TTL.DEFAULT);
    return result;
  } catch (e) { return staleData || []; }
};

export const fetchCandidateSpots = async (bounds: MapBounds, filters: { minDistanceHouses: number, nearWater: boolean }, existingIds: string[] = [], signal?: AbortSignal) => {
  const bbox = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  // ~200m buffer so building/landuse data extends beyond the viewport edge
  const bufferLat = 0.0018;
  const bufferLon = 0.003;
  const bufferedBbox = `${bounds.latMin - bufferLat},${bounds.lonMin - bufferLon},${bounds.latMax + bufferLat},${bounds.lonMax + bufferLon}`;
  try {
    const query = `[out:json][timeout:90];
      (
        way["highway"~"track|service|unclassified"](${bbox});
        node["highway"~"turning_circle|parking"](${bbox});
        node["amenity"="parking"](${bbox});
        node["informal"="yes"](${bbox});
      );
      out body geom;
      (
        node["building"](${bufferedBbox});
        way["building"](${bufferedBbox});
        way["landuse"~"residential|farmyard"](${bufferedBbox});
      );
      out center geom;`;

    const data = await robustOverpassQuery(query, lastOverpassDebug, signal);
    // Wider road context used to detect junction-like endpoints reliably.
    // IMPORTANT: includes path/footway/cycleway so that intersections with trails are detected as junctions.
    const roadContextQuery = `[out:json][timeout:45];
      way["highway"~"motorway|trunk|primary|secondary|tertiary|residential|unclassified|service|track|path|footway|cycleway|bridleway|pedestrian|steps"](${bbox});
      out body geom;`;
    const roadContext = await robustOverpassQuery(roadContextQuery, lastOverpassDebug, signal).catch(() => ({ elements: [] }));
    const nvdbRes = await fetchNVDBData(bounds).catch(() => ({ objects: [] }));
    return processCombinedData(data.elements || [], nvdbRes.objects || [], bounds, existingIds, filters, roadContext.elements || []);
  } catch (error: any) { return []; }
};

const processCombinedData = (
  elements: any[],
  nvdbObjects: NVDBObject[],
  originalBounds: MapBounds,
  existingIds: string[],
  filters: { minDistanceHouses: number, nearWater: boolean },
  roadContextElements: any[] = []
) => {
  const minDistanceHouses = Math.max(150, Number(filters?.minDistanceHouses || 150));
  const isMajorHighway = (h: string) => ['motorway', 'trunk', 'primary', 'secondary'].includes(String(h || '').toLowerCase());
  // Friluftsloven §9: 150m gjelder kun "bebodd hus eller hytte" – ikke skur,
  // garasje, låve, næring osv. `buildings` brukes til tettstedsdeteksjon
  // (alle teller); `inhabitedBuildings` brukes til `nearestHouse`-måling.
  const INHABITED_BUILDING_TAGS = new Set([
    'house', 'detached', 'semidetached_house', 'terrace', 'apartments',
    'residential', 'bungalow', 'cabin', 'hut', 'farmhouse', 'static_caravan',
    'dormitory', 'hotel', 'yes',
  ]);
  const NON_INHABITED_BUILDING_TAGS = new Set([
    'garage', 'garages', 'shed', 'barn', 'stable', 'cowshed', 'sty',
    'greenhouse', 'storage_tank', 'silo', 'hangar', 'industrial', 'commercial',
    'retail', 'warehouse', 'service', 'roof', 'carport', 'parking', 'kiosk',
    'shelter', 'public', 'civic', 'school', 'hospital', 'church', 'chapel',
    'mosque', 'temple', 'synagogue', 'transformer_tower', 'water_tower',
    'gatehouse', 'boathouse',
  ]);
  const isInhabitedBuilding = (el: any) => {
    const b = el.tags?.building;
    if (!b) return false;
    if (NON_INHABITED_BUILDING_TAGS.has(b)) return false;
    return INHABITED_BUILDING_TAGS.has(b);
  };
  const buildings = elements.filter(e => e.tags?.building || e.tags?.landuse === 'residential');
  const inhabitedBuildings = elements.filter(isInhabitedBuilding);

  // Extract residential/farmyard polygons for point-in-polygon checks.
  // These catch spots near untagged houses (common in rural Norway).
  const residentialPolygons: {lat: number, lon: number}[][] = elements
    .filter(e =>
      e.type === 'way' &&
      Array.isArray(e.geometry) &&
      e.geometry.length >= 3 &&
      (e.tags?.landuse === 'residential' || e.tags?.landuse === 'farmyard')
    )
    .map(e => e.geometry as {lat: number, lon: number}[]);

  const isInResidentialArea = (lat: number, lon: number): boolean => {
    for (const polygon of residentialPolygons) {
      if (isPointInPolygon(lat, lon, polygon)) return true;
    }
    return false;
  };

  // Separate ways and nodes to handle them differently
  const ways = elements.filter(e => e.type === 'way' && (e.tags?.highway || e.tags?.tourism || e.tags?.informal));
  const candidateWays = ways.filter((w) => ['track', 'service', 'unclassified'].includes(String(w.tags?.highway || '').toLowerCase()));
  const contextWays = (Array.isArray(roadContextElements) && roadContextElements.length > 0)
    ? roadContextElements.filter((e) => e.type === 'way' && e.tags?.highway)
    : ways.filter((e) => e.type === 'way' && e.tags?.highway);
  const nodes = elements.filter(e => e.type === 'node' && (e.tags?.highway || e.tags?.amenity || e.tags?.tourism || e.tags?.informal));

  const nodeUsage = new Map<number, number>();
  const nodeParentWayHighways = new Map<number, Set<string>>();
  const nodeParentWayAccess = new Map<number, Set<string>>();

  // Tell node-bruk for å finne blindveier.
  // Merge contextWays with any initial-query ways not already present, to ensure
  // all road/path types contribute to junction detection.
  const contextWayIds = new Set(contextWays.map((w: any) => w.id));
  const allWaysForJunction = [...contextWays];
  ways.forEach(w => {
    if (!contextWayIds.has(w.id)) allWaysForJunction.push(w);
  });

  allWaysForJunction.forEach((r: any) => {
    if (!Array.isArray(r.nodes)) return;
    const highway = String(r.tags?.highway || '').toLowerCase();
    const access = String(r.tags?.access || '').toLowerCase();
    r.nodes.forEach((id: number) => {
      nodeUsage.set(id, (nodeUsage.get(id) || 0) + 1);
      if (!nodeParentWayHighways.has(id)) nodeParentWayHighways.set(id, new Set<string>());
      if (highway) nodeParentWayHighways.get(id)!.add(highway);
      if (!nodeParentWayAccess.has(id)) nodeParentWayAccess.set(id, new Set<string>());
      if (access) nodeParentWayAccess.get(id)!.add(access);
    });
  });

  const finalSpots: CampingSpot[] = [];
  const processedIds = new Set(existingIds);

  nvdbObjects.forEach(obj => {
    // Skip NVDB turning circles (typeId 40) — road geometry artifacts with no camping value.
    // Real rest areas (typeId 39) with facilities are kept.
    if (obj.typeId === 40) return;

    const spotId = `nvdb-${obj.id}`; if (processedIds.has(spotId)) return;
    const houseDist = checkMinDistance(obj.location.latitude, obj.location.longitude, inhabitedBuildings);
    processedIds.add(spotId);

    finalSpots.push({
      id: spotId, source: 'nvdb', externalId: String(obj.id), importVersion: 'nvdb-v4', lastSyncedAt: new Date().toISOString(), name: obj.name || (obj.typeId === 40 ? i18n.t('spot.categories.turning_circle') : i18n.t('spot.categories.rest_area')), description: obj.typeId === 40 ? i18n.t('spot.description_turning_circle') : i18n.t('spot.description_rest_area'),
      spotType: 'rasteplass', sourceLevel: 1, location: obj.location, score: obj.typeId === 40 ? 0 : 7, suitabilityColor: '#2563eb',
      features: ["NVDB", obj.typeId === 40 ? i18n.t('spot.categories.turning_circle').toUpperCase() : i18n.t('spot.categories.rest_area').toUpperCase()], reasoning: i18n.t('spot.reasoning_svv'),
      nearestHouse: Math.round(houseDist), highway: i18n.t('types.road'), surfaceType: 'Asfalt', isPublicAccess: true,
      buildingProximity: houseDist < 150 ? i18n.t('spot.too_close') : "OK", trafficRisk: i18n.t('spot.traffic_low'),
      legal: { isUtmark: true, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false }
    });
  });

  // 1. Process Standalone Nodes (Parking, Turning Circles)
  nodes.forEach(e => {
    const lat = e.lat; const lon = e.lon;
    if (!lat || !lon) return;

    // STRICT FILTER 1: Avoid Main Roads (Primary/Trunk/Secondary)
    // Often turning circles are just junctions on main roads.
    // This requires node-way relation check, but as heuristic we skip 'highway=turning_circle' on nodes without other context if we suspect it's busy.
    // Since we don't have parent way info easily for nodes in this structure without `recurse`, we rely on other filters.
    // BUT we can check if tags indicate it's huge.

    const hDist = checkMinDistance(lat, lon, inhabitedBuildings);

    // STRICT FILTER 2: House Distance (150m Absolute, no exceptions)
    if (hDist < minDistanceHouses) return;

    // STRICT FILTER: Reject if inside a residential/farmyard area (catches untagged buildings)
    if (isInResidentialArea(lat, lon)) return;

    const spotId = `osm-node-${e.id}`;
    if (processedIds.has(spotId)) return;

    // STRICT FILTER: skip nodes at/near intersections (multiple nearby roads).
    // This avoids "good space in road junction" false positives.
    const roadsNearNode = contextWays.filter((w) => {
      if (!Array.isArray(w.geometry) || w.geometry.length === 0) return false;
      return w.geometry.some((p: any) => getDistance(lat, lon, p.lat, p.lon) <= 30);
    }).length;
    if (roadsNearNode >= 2) return;

    // If this exact node is referenced by multiple ways, it is a junction.
    const wayUsage = nodeUsage.get(e.id) || 0;
    if (wayUsage >= 2) return;
    const parentHighways = nodeParentWayHighways.get(e.id);
    if (parentHighways && [...parentHighways].some(isMajorHighway)) return;

    // STRICT FILTER: Skip nodes on restricted-access roads
    const nodeAccess = nodeParentWayAccess.get(e.id);
    if (nodeAccess && [...nodeAccess].some(a => ['private', 'no', 'destination', 'customers', 'permit'].includes(a))) return;

    // Filter out private parking
    if (e.tags?.access === 'private' || e.tags?.access === 'customers' || e.tags?.fee === 'yes') return;

    // Determine the actual nearest road type for this node
    let nearestHighway = 'unknown';
    let nearestRoadSurface = e.tags?.surface || 'Ukjent';
    let nearestRoadDist = Infinity;
    contextWays.forEach((w: any) => {
      if (!Array.isArray(w.geometry)) return;
      w.geometry.forEach((p: any) => {
        const d = getDistance(lat, lon, p.lat, p.lon);
        if (d < nearestRoadDist) {
          nearestRoadDist = d;
          nearestHighway = String(w.tags?.highway || 'unknown').toLowerCase();
          if (w.tags?.surface) nearestRoadSurface = w.tags.surface;
        }
      });
    });

    // STRICT FILTER: Skip parking nodes that are only near trails/footways (not car-accessible)
    const isTrailOnly = ['path', 'footway', 'cycleway', 'bridleway', 'pedestrian', 'steps'].includes(nearestHighway);
    if (isTrailOnly) return; // Trail parking is not suitable for fricamping

    let localizedLabel = i18n.t('spot.categories.parking').toUpperCase();
    let score = 4;
    let feat = i18n.t('overpass.feature_parking');

    if (e.tags?.highway === 'turning_circle') {
      localizedLabel = i18n.t('spot.categories.turning_circle').toUpperCase();
      feat = i18n.t('overpass.feature_turning');
    }
    else if (e.tags?.informal === 'yes') {
      localizedLabel = i18n.t('spot.categories.informal').toUpperCase();
      feat = "FRICAMP";
      score = 6;
    }
    else {
      localizedLabel = i18n.t('spot.categories.parking').toUpperCase();
    }

    processedIds.add(spotId);
    finalSpots.push({
      id: spotId, source: 'osm', externalId: String(e.id), importVersion: 'osm-overpass', lastSyncedAt: new Date().toISOString(), name: localizedLabel, description: i18n.t('spot.description_osm', { feature: feat.toLowerCase() }),
      location: { latitude: lat, longitude: lon }, score, spotType: 'algorithmic', sourceLevel: 2,
      suitabilityColor: score > 5 ? BRAND : '#f59e0b',
      features: [feat, "OSM"], reasoning: i18n.t('spot.reasoning_map'), nearestHouse: Math.round(hDist),
      highway: nearestHighway, surfaceType: nearestRoadSurface, isPublicAccess: true,
      isDriveable: DRIVEABLE_HIGHWAYS.has(nearestHighway),
      trafficRisk: i18n.t('overpass.traffic.unknown'), buildingProximity: "OK",
      legal: { isUtmark: true, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false }
    });
  });

  // 2. Process Ways (Blind Roads & Ends)
  candidateWays.forEach(e => {
    // STRICT FILTER 3: Road Quality
    // Exclude 'track' if grade4/5 (bad quality) or missing surface on tracks unless explicitly allowed
    if (e.tags?.highway === 'track') {
      const grade = e.tags.tracktype;
      if (grade === 'grade4' || grade === 'grade5') return; // Ubrukelig traktorvei
    }

    // STRICT FILTER 4: Main Roads
    // Avoid picking points on primary/secondary infrastructure
    if (['primary', 'secondary', 'trunk', 'motorway'].includes(e.tags?.highway)) return;

    // Sjekk for blindveier (highway track/service/unclassified)
    if (Array.isArray(e.nodes) && Array.isArray(e.geometry)) {
      const ends = [0, e.nodes.length - 1];
      ends.forEach(idx => {
        const nodeId = e.nodes[idx];
        const usage = nodeUsage.get(nodeId);
        // Hvis noden brukes av mer enn én vei, er det et kryss.
        if (usage !== 1) return;
        {
          const p = e.geometry[idx]; if (!p) return;
          const hDist = checkMinDistance(p.lat, p.lon, inhabitedBuildings);

          // STRICT FILTER 5: House Distance for Ends (150m)
          if (hDist < minDistanceHouses) return;

          // STRICT FILTER: Reject road ends inside residential/farmyard areas
          if (isInResidentialArea(p.lat, p.lon)) return;

          // STRICT FILTER 6: End-point must not be junction-like in broader road context.
          // Exclude the candidate way itself so we detect OTHER roads near this endpoint.
          // A dead-end with no other roads nearby = 0 other roads = OK.
          // A T-intersection or junction = 1+ other roads nearby = REJECT.
          const otherRoadsNearEnd = contextWays.filter((w) => {
            if (w.id === e.id) return false; // Exclude the road itself
            if (!Array.isArray(w.geometry) || w.geometry.length === 0) return false;
            return w.geometry.some((gp: any) => getDistance(p.lat, p.lon, gp.lat, gp.lon) <= 25);
          }).length;
          if (otherRoadsNearEnd >= 1) return; // Any other road near endpoint = junction/T-kryss
          const parentWays = nodeParentWayHighways.get(nodeId);
          if (!parentWays || parentWays.size !== 1) return;
          if ([...parentWays].some(isMajorHighway)) return;

          // Skip dead-ends on residential roads (these almost always lead to houses)
          if ([...parentWays].includes('residential')) return;

          const spotId = `osm-end-${nodeId}`;
          if (processedIds.has(spotId)) return;

          // Unngå private veier
          if (e.tags?.access === 'private' || e.tags?.access === 'no') return;

          // Skip road ends with restricted access on parent way
          const endAccess = nodeParentWayAccess.get(nodeId);
          if (endAccess && [...endAccess].some(a => ['private', 'no', 'destination', 'customers', 'permit'].includes(a))) return;

          processedIds.add(spotId);
          finalSpots.push({
            id: spotId,
            source: 'osm',
            externalId: String(nodeId),
            importVersion: 'osm-overpass',
            lastSyncedAt: new Date().toISOString(),
            name: e.tags?.highway === 'track' ? i18n.t('spot.categories.forest_road') : i18n.t('spot.categories.road_end'),
            location: { latitude: p.lat, longitude: p.lon },
            score: 6,
            spotType: 'algorithmic', sourceLevel: 2, suitabilityColor: BRAND,
            features: [i18n.t('spot.categories.road_end').toUpperCase(), i18n.t('spot.wilderness')], reasoning: i18n.t('spot.reasoning_road_end'), nearestHouse: Math.round(hDist),
            highway: e.tags?.highway || i18n.t('types.road'), surfaceType: e.tags?.surface || i18n.t('spot.surface_gravel'), isPublicAccess: true,
            isRoadEnd: true, nearbyRoadCount: otherRoadsNearEnd,
            description: e.tags?.highway === 'track' ? i18n.t('spot.description_forest_road') : i18n.t('spot.description_road_end'),
            trafficRisk: i18n.t('spot.traffic_low'),
            buildingProximity: "OK",
            legal: { isUtmark: true, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false }
          });
        }
      });
    }
  });

  return finalSpots.map((spot) => ({
    ...spot,
    ...deriveRoadAccessClass(spot),
  }));
};

export const fetchNearestBuildingDist = async (coords: Coordinates): Promise<number> => {
  const query = `[out:json][timeout:10];(node["building"](around:500, ${coords.latitude}, ${coords.longitude});way["building"](around:500, ${coords.latitude}, ${coords.longitude}););out center;`;
  try {
    const data = await robustOverpassQuery(query, { lastQuery: "", lastUrl: "", lastResponse: null, lastError: null });
    const buildings = data.elements || [];
    let minDist = 999;
    buildings.forEach((b: any) => {
      const lat = b.lat || b.center?.lat; const lon = b.lon || b.center?.lon;
      if (lat && lon) { const d = getDistance(coords.latitude, coords.longitude, lat, lon); if (d < minDist) minDist = d; }
    });
    return Math.round(minDist);
  } catch (e) { return 999; }
};


export const findNearestRoadForPoint = async (coords: Coordinates): Promise<{
  highway: string, surface: string, geometry: Coordinates[], distance: number,
  isDriveable: boolean, nearestDriveableHighway: string | null, nearestDriveableDist: number | null
} | null> => {
  const query = `[out:json][timeout:10];way["highway"](around:150, ${coords.latitude}, ${coords.longitude});out geom;`;
  try {
    const data = await robustOverpassQuery(query, { lastQuery: "", lastUrl: "", lastResponse: null, lastError: null });
    const roads = data.elements || [];
    let closestRoad: any = null; let minDist = Infinity;
    let closestDriveableRoad: any = null; let minDriveableDist = Infinity;

    roads.forEach((r: any) => {
      if (!Array.isArray(r.geometry)) return;
      const hw = String(r.tags?.highway || '').toLowerCase();
      r.geometry.forEach((p: any) => {
        const d = getDistance(coords.latitude, coords.longitude, p.lat, p.lon);
        if (d < minDist) { minDist = d; closestRoad = r; }
        if (DRIVEABLE_HIGHWAYS.has(hw) && d < minDriveableDist) {
          minDriveableDist = d; closestDriveableRoad = r;
        }
      });
    });
    if (closestRoad) {
      const hw = String(closestRoad.tags?.highway || '').toLowerCase();
      const isDriveable = DRIVEABLE_HIGHWAYS.has(hw);
      return {
        highway: closestRoad.tags.highway || i18n.t('overpass.road_fallback'),
        surface: closestRoad.tags.surface || i18n.t('overpass.surface.gravel'),
        geometry: closestRoad.geometry.map((p: any) => ({ latitude: p.lat, longitude: p.lon })),
        distance: minDist,
        isDriveable,
        nearestDriveableHighway: closestDriveableRoad ? closestDriveableRoad.tags.highway : null,
        nearestDriveableDist: closestDriveableRoad ? Math.round(minDriveableDist) : null
      };
    }
  } catch (e) { }
  return null;
};

/**
 * Fetches road info AND nearest building distance in a single direct Overpass call.
 * Bypasses the global serial queue — use for user-initiated manual analysis only.
 */
export const fetchRoadAndBuildingForPoint = async (coords: Coordinates): Promise<{
  roadInfo: { highway: string; surface: string; geometry: Coordinates[]; distance: number; isDriveable: boolean; nearestDriveableHighway: string | null; nearestDriveableDist: number | null } | null;
  buildingDist: number;
}> => {
  const { latitude: lat, longitude: lon } = coords;
  // Use 'out geom tags' (not 'center geom') — guarantees geometry array for all ways
  const query = `[out:json][timeout:12];(way["highway"](around:150,${lat},${lon});node["building"](around:500,${lat},${lon});way["building"](around:500,${lat},${lon}););out geom tags;`;
  try {
    const data = await directOverpassFetch(query);
    const elements = data.elements || [];

    // --- Road info (ways with highway tag + geometry) ---
    let closestRoad: any = null; let minRoadDist = Infinity;
    let closestDriveableRoad: any = null; let minDriveableDist = Infinity;
    elements.forEach((e: any) => {
      if (!e.tags?.highway || !Array.isArray(e.geometry)) return;
      const hw = String(e.tags.highway || '').toLowerCase();
      e.geometry.forEach((p: any) => {
        const d = getDistance(lat, lon, p.lat, p.lon);
        if (d < minRoadDist) { minRoadDist = d; closestRoad = e; }
        if (DRIVEABLE_HIGHWAYS.has(hw) && d < minDriveableDist) { minDriveableDist = d; closestDriveableRoad = e; }
      });
    });
    let roadInfo = null;
    if (closestRoad) {
      const hw = String(closestRoad.tags.highway || '').toLowerCase();
      roadInfo = {
        highway: closestRoad.tags.highway || i18n.t('overpass.road_fallback'),
        surface: closestRoad.tags.surface || i18n.t('overpass.surface.gravel'),
        geometry: closestRoad.geometry.map((p: any) => ({ latitude: p.lat, longitude: p.lon })),
        distance: minRoadDist,
        isDriveable: DRIVEABLE_HIGHWAYS.has(hw),
        nearestDriveableHighway: closestDriveableRoad ? closestDriveableRoad.tags.highway : null,
        nearestDriveableDist: closestDriveableRoad ? Math.round(minDriveableDist) : null,
      };
    }

    // --- Building distance (nodes: direct lat/lon; ways: first geometry node) ---
    let minBuildingDist = 999;
    elements.forEach((b: any) => {
      if (!b.tags?.building || b.tags?.highway) return;
      const bLat = b.lat ?? b.geometry?.[0]?.lat;
      const bLon = b.lon ?? b.geometry?.[0]?.lon;
      if (bLat != null && bLon != null) {
        const d = getDistance(lat, lon, bLat, bLon);
        if (d < minBuildingDist) minBuildingDist = d;
      }
    });

    return { roadInfo, buildingDist: Math.round(minBuildingDist) };
  } catch {
    return { roadInfo: null, buildingDist: 999 };
  }
};

/**
 * Sample road surfaces along a route by querying Overpass at evenly-spaced points.
 * Works globally (not limited to Norway). Returns a summary of surface types and highway classes.
 */
export const sampleRouteSurfaces = async (
  flatCoords: [number, number][],
  sampleCount = 12
): Promise<{ surfaces: Record<string, number>; highways: Record<string, number>; samplesTaken: number }> => {
  const step = Math.max(1, Math.floor(flatCoords.length / sampleCount));
  const points: [number, number][] = [];
  for (let i = 0; i < flatCoords.length; i += step) {
    points.push(flatCoords[i]);
    if (points.length >= sampleCount) break;
  }

  const surfaces: Record<string, number> = {};
  const highways: Record<string, number> = {};
  let samplesTaken = 0;

  // Batch points into groups of 4 to reduce Overpass calls
  for (let i = 0; i < points.length; i += 4) {
    const batch = points.slice(i, i + 4);
    const aroundClauses = batch.map(([lat, lon]) =>
      `way["highway"~"motorway|trunk|primary|secondary|tertiary|unclassified|residential|service|track"](around:80,${lat},${lon});`
    ).join('\n');
    const query = `[out:json][timeout:15];(\n${aroundClauses}\n);out tags;`;

    try {
      const data = await robustOverpassQuery(query, { lastQuery: '', lastUrl: '', lastResponse: null, lastError: null });
      const seen = new Set<number>();
      for (const el of (data.elements || [])) {
        if (seen.has(el.id)) continue;
        seen.add(el.id);
        const s = el.tags?.surface || 'unknown';
        const h = el.tags?.highway || 'unknown';
        surfaces[s] = (surfaces[s] || 0) + 1;
        highways[h] = (highways[h] || 0) + 1;
      }
      samplesTaken += batch.length;
    } catch {
      // Overpass may fail for some batches — continue with others
    }
  }

  return { surfaces, highways, samplesTaken };
};

export const fetchOSMAmenities = async (bounds: MapBounds): Promise<CampingSpot[]> => {
  const cacheKey = `overpass_amenities_v1_${snapBboxToGrid(bounds, 0.05)}`;
  const { data: staleData, isStale } = getCacheStale(cacheKey);
  if (staleData && !isStale) return staleData;

  const bbox = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  const query = `[out:json][timeout:15];(node["amenity"~"picnic_site|shelter"](${bbox});node["leisure"="firepit"](${bbox});node["tourism"~"shelter|viewpoint"](${bbox}););out center body;`;
  try {
    const data = await robustOverpassQuery(query, { lastQuery: "", lastUrl: "", lastResponse: null, lastError: null });
    const result = (data.elements || []).map((e: any) => {
      const lat = e.lat || e.center?.lat; const lon = e.lon || e.center?.lon;
      if (!lat || !lon) return null;
      const tags = e.tags || {};
      let type = i18n.t('overpass.type_picnic');
      if (tags.tourism === 'viewpoint') type = i18n.t('overpass.type_viewpoint');
      else if (tags.tourism === 'shelter' || tags.amenity === 'shelter') type = i18n.t('overpass.type_shelter');
      else if (tags.leisure === 'firepit') type = i18n.t('spot.categories.firepit');
      return {
        id: `osm-amenity-${e.id}`, source: 'osm', externalId: String(e.id), importVersion: 'osm-overpass', lastSyncedAt: new Date().toISOString(), name: tags.name || (type === i18n.t('overpass.type_viewpoint') ? i18n.t('spot.categories.viewpoint') : type), location: { latitude: lat, longitude: lon },
        score: 8, spotType: 'osm_amenity', sourceLevel: 3, suitabilityColor: BRAND,
        features: [type.toUpperCase(), "OSM"], reasoning: i18n.t('spot.reasoning_map'), nearestHouse: 999,
        // Added missing mandatory properties to satisfy CampingSpot interface
        description: i18n.t('spot.description_amenity', { type: type.toLowerCase() }),
        trafficRisk: "N/A",
        buildingProximity: "N/A",
        highway: i18n.t('spot.nature'), surfaceType: i18n.t('spot.nature'), isPublicAccess: true,
        legal: { isUtmark: true, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false }
      } as CampingSpot;
    }).filter((s): s is CampingSpot => s !== null);
    setCache(cacheKey, result, CACHE_TTL.DEFAULT);
    return result;
  } catch (e) { return staleData || []; }
};

export const fetchOSMDumpStations = async (bounds: MapBounds): Promise<CampingSpot[]> => {
  const cacheKey = `overpass_dump_v1_${snapBboxToGrid(bounds, 0.05)}`;
  const { data: staleData, isStale } = getCacheStale(cacheKey);
  if (staleData && !isStale) return staleData;

  const bbox = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  const query = `[out:json][timeout:15];(node["amenity"="sanitary_dump_station"](${bbox});way["amenity"="sanitary_dump_station"](${bbox}););out center body;`;
  try {
    const data = await robustOverpassQuery(query, { lastQuery: "", lastUrl: "", lastResponse: null, lastError: null });
    const result = (data.elements || []).map((e: any) => {
      const lat = e.lat || e.center?.lat; const lon = e.lon || e.center?.lon;
      if (!lat || !lon) return null;
      const type = i18n.t('spot.categories.dump_station');
      return {
        id: `osm-dump-${e.id}`, source: 'osm', externalId: String(e.id), importVersion: 'osm-overpass', lastSyncedAt: new Date().toISOString(), name: type, location: { latitude: lat, longitude: lon },
        score: 8, spotType: 'osm_amenity', sourceLevel: 3, suitabilityColor: '#8b5cf6', // Matching color from CATEGORY_MAP
        features: ["DUMP_STATION", "OSM"], reasoning: i18n.t('spot.reasoning_map'), nearestHouse: 999,
        description: i18n.t('spot.categories.dump_station'),
        trafficRisk: "N/A",
        buildingProximity: "N/A",
        highway: "N/A", surfaceType: "Ukjent", isPublicAccess: true,
        legal: { isUtmark: false, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false },
        tags: ["tømmestasjon"]
      } as CampingSpot;
    }).filter((s): s is CampingSpot => s !== null);
    setCache(cacheKey, result, CACHE_TTL.DEFAULT);
    return result;
  } catch (e) { return staleData || []; }
};

export const fetchCommercialCampsites = async (bounds: MapBounds): Promise<CampingSpot[]> => {
  const cacheKey = `overpass_campsites_v1_${snapBboxToGrid(bounds, 0.05)}`;
  const { data: staleData, isStale } = getCacheStale(cacheKey);
  if (staleData && !isStale) return staleData;

  const bbox = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  const query = `[out:json][timeout:15];(node["tourism"~"camp_site|caravan_site"](${bbox}););out center body;`;
  try {
    const data = await robustOverpassQuery(query, { lastQuery: "", lastUrl: "", lastResponse: null, lastError: null });
    const result = (data.elements || []).map((e: any) => {
      const lat = e.lat || e.center?.lat; const lon = e.lon || e.center?.lon;
      if (!lat || !lon) return null;
      const tags = e.tags || {};
      return {
        id: `osm-camp-${e.id}`, source: 'osm', externalId: String(e.id), importVersion: 'osm-overpass', lastSyncedAt: new Date().toISOString(), name: tags.name || i18n.t('spot.categories.wildcamp'),
        location: { latitude: lat, longitude: lon }, score: 10, spotType: 'commercial_campsite', sourceLevel: 3, suitabilityColor: '#1565C0',
        features: [i18n.t('route.camping').toUpperCase()], reasoning: i18n.t('spot.reasoning_map'), nearestHouse: 999, highway: i18n.t('types.road'), surfaceType: 'Asfalt', isPublicAccess: true,
        // Added missing mandatory properties to satisfy CampingSpot interface
        description: i18n.t('spot.description_commercial'),
        trafficRisk: i18n.t('spot.traffic_normal'),
        buildingProximity: "N/A",
        commercialMetadata: { phone: tags.phone, website: tags.website, fee: tags.fee, opening_hours: tags.opening_hours || undefined },
        legal: { isUtmark: false, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false }
      } as CampingSpot;
    }).filter((s): s is CampingSpot => s !== null);
    setCache(cacheKey, result, CACHE_TTL.DEFAULT);
    return result;
  } catch (e) { return staleData || []; }
};

export type NearbySpotAmenity = {
  name?: string;
  distanceM: number;
  location: Coordinates;
  osmId: string;
  openingHours?: string;
};

export type NearbySpotAmenities = {
  grocery?: NearbySpotAmenity;
  playground?: NearbySpotAmenity;
};

const getElementCenter = (element: any): Coordinates | null => {
  const lat = element.lat || element.center?.lat;
  const lon = element.lon || element.center?.lon;
  if (typeof lat !== 'number' || typeof lon !== 'number') return null;
  return { latitude: lat, longitude: lon };
};

const formatOsmId = (element: any) => `${element.type || 'node'}-${element.id}`;

export const fetchNearestSpotAmenities = async (
  location: Coordinates,
  radiusMeters = 12000
): Promise<NearbySpotAmenities> => {
  const latKey = location.latitude.toFixed(3);
  const lonKey = location.longitude.toFixed(3);
  const radius = Math.max(500, Math.min(20000, Math.round(radiusMeters)));
  const cacheKey = `nearest_spot_amenities_v1_${latKey}_${lonKey}_${radius}`;
  const { data: staleData, isStale } = getCacheStale(cacheKey);
  if (staleData && !isStale) return staleData;

  const lat = location.latitude;
  const lon = location.longitude;
  const query = `[out:json][timeout:20];(
node(around:${radius},${lat},${lon})["shop"~"supermarket|convenience|grocery"];
way(around:${radius},${lat},${lon})["shop"~"supermarket|convenience|grocery"];
node(around:${radius},${lat},${lon})["leisure"="playground"];
way(around:${radius},${lat},${lon})["leisure"="playground"];
);out center tags;`;

  try {
    const data = await robustOverpassQuery(query, { lastQuery: "", lastUrl: "", lastResponse: null, lastError: null });
    const result: NearbySpotAmenities = {};

    for (const element of (data.elements || [])) {
      const center = getElementCenter(element);
      if (!center) continue;
      const tags = element.tags || {};
      const distanceM = Math.round(getDistance(lat, lon, center.latitude, center.longitude));
      const item: NearbySpotAmenity = {
        name: tags.name,
        distanceM,
        location: center,
        osmId: formatOsmId(element),
        openingHours: tags.opening_hours || undefined,
      };

      if (tags.shop && /^(supermarket|convenience|grocery)$/.test(String(tags.shop))) {
        if (!result.grocery || distanceM < result.grocery.distanceM) result.grocery = item;
      } else if (tags.leisure === 'playground') {
        if (!result.playground || distanceM < result.playground.distanceM) result.playground = item;
      }
    }

    setCache(cacheKey, result, CACHE_TTL.LONG);
    return result;
  } catch (e) {
    return staleData || {};
  }
};

/** Combined single-query fetch for campsites, amenities, dump stations and playgrounds (reduces Overpass calls) */
export const fetchBatchedOverpassPOIs = async (
  bounds: MapBounds,
  active: { amenities: boolean; dumpStations: boolean; campsites: boolean; playgrounds: boolean } = { amenities: true, dumpStations: true, campsites: true, playgrounds: true }
): Promise<{ campsites: CampingSpot[], amenities: CampingSpot[], dumpStations: CampingSpot[], playgrounds: CampingSpot[] }> => {
  const activeKey = `${active.campsites ? 'c' : ''}${active.amenities ? 'a' : ''}${active.dumpStations ? 'd' : ''}${active.playgrounds ? 'p' : ''}`;
  const cacheKey = `overpass_batch_pois_v4_${activeKey}_${snapBboxToGrid(bounds, 0.05)}`;
  const { data: staleData, isStale } = getCacheStale(cacheKey);
  if (staleData && !isStale) return staleData;

  const bbox = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  const parts: string[] = [];
  if (active.campsites) parts.push(`node["tourism"~"camp_site|caravan_site"](${bbox})`);
  if (active.amenities) {
    parts.push(`node["amenity"~"picnic_site|shelter"](${bbox})`);
    parts.push(`node["leisure"="firepit"](${bbox})`);
    parts.push(`node["tourism"~"shelter|viewpoint"](${bbox})`);
  }
  if (active.dumpStations) {
    parts.push(`node["amenity"="sanitary_dump_station"](${bbox})`);
    parts.push(`way["amenity"="sanitary_dump_station"](${bbox})`);
  }
  if (active.playgrounds) {
    parts.push(`node["leisure"="playground"](${bbox})`);
    parts.push(`way["leisure"="playground"](${bbox})`);
  }
  if (parts.length === 0) return { campsites: [], amenities: [], dumpStations: [], playgrounds: [] };
  const query = `[out:json][timeout:30];(${parts.join(';')};);out center body;`;
  try {
    const data = await robustOverpassQuery(query, { lastQuery: "", lastUrl: "", lastResponse: null, lastError: null });
    const campsites: CampingSpot[] = [];
    const amenities: CampingSpot[] = [];
    const dumpStations: CampingSpot[] = [];
    const playgrounds: CampingSpot[] = [];
    (data.elements || []).forEach((e: any) => {
      const lat = e.lat || e.center?.lat; const lon = e.lon || e.center?.lon;
      if (!lat || !lon) return;
      const tags = e.tags || {};
      const isCampsite = tags.tourism === 'camp_site' || tags.tourism === 'caravan_site';
      const isDumpStation = tags.amenity === 'sanitary_dump_station';
      const isPlayground = tags.leisure === 'playground';
      if (isCampsite) {
        campsites.push({
          id: `osm-camp-${e.id}`, source: 'osm', externalId: String(e.id), importVersion: 'osm-overpass', lastSyncedAt: new Date().toISOString(),
          name: tags.name || i18n.t('spot.categories.wildcamp'),
          location: { latitude: lat, longitude: lon }, score: 10, spotType: 'commercial_campsite', sourceLevel: 3, suitabilityColor: '#1565C0',
          features: [i18n.t('route.camping').toUpperCase()], reasoning: i18n.t('spot.reasoning_map'), nearestHouse: 999, highway: i18n.t('types.road'), surfaceType: 'Asfalt', isPublicAccess: true,
          description: i18n.t('spot.description_commercial'), trafficRisk: i18n.t('spot.traffic_normal'), buildingProximity: "N/A",
          commercialMetadata: { phone: tags.phone, website: tags.website, fee: tags.fee, opening_hours: tags.opening_hours || undefined },
          legal: { isUtmark: false, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false }
        } as CampingSpot);
      } else if (isDumpStation) {
        const type = i18n.t('spot.categories.dump_station');
        dumpStations.push({
          id: `osm-dump-${e.id}`, source: 'osm', externalId: String(e.id), importVersion: 'osm-overpass', lastSyncedAt: new Date().toISOString(), name: type,
          location: { latitude: lat, longitude: lon }, score: 8, spotType: 'osm_amenity', sourceLevel: 3, suitabilityColor: '#8b5cf6',
          features: ["DUMP_STATION", "OSM"], reasoning: i18n.t('spot.reasoning_map'), nearestHouse: 999,
          description: i18n.t('spot.categories.dump_station'), trafficRisk: "N/A", buildingProximity: "N/A",
          highway: "N/A", surfaceType: "Ukjent", isPublicAccess: true,
          legal: { isUtmark: false, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false },
          tags: ["tømmestasjon"]
        } as CampingSpot);
      } else if (isPlayground) {
        const type = i18n.t('spot.categories.playground', 'Lekeplass');
        playgrounds.push({
          id: `osm-playground-${e.id}`, source: 'osm', externalId: String(e.id), importVersion: 'osm-overpass', lastSyncedAt: new Date().toISOString(), name: tags.name || type,
          location: { latitude: lat, longitude: lon }, score: 7, spotType: 'osm_amenity', sourceLevel: 3, suitabilityColor: '#f59e0b',
          features: ["LEKEPLASS", "OSM"], reasoning: i18n.t('spot.reasoning_map'), nearestHouse: 999,
          description: i18n.t('spot.categories.playground', 'Lekeplass'), trafficRisk: "N/A", buildingProximity: "N/A",
          highway: "N/A", surfaceType: "Ukjent", isPublicAccess: true,
          legal: { isUtmark: false, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false },
          tags: ["lekeplass"]
        } as CampingSpot);
      } else {
        let type = i18n.t('overpass.type_picnic');
        if (tags.tourism === 'viewpoint') type = i18n.t('overpass.type_viewpoint');
        else if (tags.tourism === 'shelter' || tags.amenity === 'shelter') type = i18n.t('overpass.type_shelter');
        else if (tags.leisure === 'firepit') type = i18n.t('spot.categories.firepit');
        amenities.push({
          id: `osm-amenity-${e.id}`, source: 'osm', externalId: String(e.id), importVersion: 'osm-overpass', lastSyncedAt: new Date().toISOString(),
          name: tags.name || (type === i18n.t('overpass.type_viewpoint') ? i18n.t('spot.categories.viewpoint') : type),
          location: { latitude: lat, longitude: lon }, score: 8, spotType: 'osm_amenity', sourceLevel: 3, suitabilityColor: BRAND,
          features: [type.toUpperCase(), "OSM"], reasoning: i18n.t('spot.reasoning_map'), nearestHouse: 999,
          description: i18n.t('spot.description_amenity', { type: type.toLowerCase() }), trafficRisk: "N/A", buildingProximity: "N/A",
          highway: i18n.t('spot.nature'), surfaceType: i18n.t('spot.nature'), isPublicAccess: true,
          legal: { isUtmark: true, inProtectedArea: false, hasBarrier: false, distToFarmland: 999, nearMajorRoad: false }
        } as CampingSpot);
      }
    });
    const result = { campsites, amenities, dumpStations, playgrounds };
    setCache(cacheKey, result, CACHE_TTL.DEFAULT);
    return result;
  } catch (e) { return staleData || { campsites: [], amenities: [], dumpStations: [], playgrounds: [] }; }
};

/** @deprecated Use fetchBatchedOverpassPOIs instead */
export const fetchAllOverpassPOIs = async (bounds: MapBounds): Promise<{ campsites: CampingSpot[], amenities: CampingSpot[] }> => {
  const result = await fetchBatchedOverpassPOIs(bounds);
  return { campsites: result.campsites, amenities: result.amenities };
};

export const fetchOSMWastePoints = async (bounds: MapBounds): Promise<WastePoint[]> => {
  const cacheKey = `overpass_waste_v1_${snapBboxToGrid(bounds, 0.05)}`;
  const { data: staleData, isStale } = getCacheStale(cacheKey);
  if (staleData && !isStale) return staleData;

  const bbox = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  const query = `[out:json][timeout:15];(node["amenity"="waste_disposal"](${bbox});node["amenity"="waste_basket"](${bbox}););out body;`;

  try {
    const data = await robustOverpassQuery(query, { lastQuery: "", lastUrl: "", lastResponse: null, lastError: null });
    const result: WastePoint[] = (data.elements || []).map((e: any) => {
      if (!e.lat || !e.lon) return null;
      const amenity = e.tags?.amenity;
      return {
        id: `osm-waste-${e.id}`,
        source: 'osm' as const,
        type: (amenity === 'waste_disposal' ? 'waste_disposal' : 'waste_basket') as WastePoint['type'],
        location: { latitude: e.lat, longitude: e.lon },
        name: e.tags?.name,
        operator: e.tags?.operator,
      };
    }).filter(Boolean) as WastePoint[];

    setCache(cacheKey, result, CACHE_TTL.DEFAULT);
    return result;
  } catch (e) {
    return staleData || [];
  }
};
